import { AfterViewInit, Component, ElementRef, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { AuthService } from '@auth/auth.service';
import { WebcomponentsService } from '@core/service/webcomponents.service';
import { LocalService } from '@home/storage/local.service';
import { ValueToPayService } from './services';

// Models
import { Payload } from '@model/asopagos/payload';
import DetailCredit from '@model/detailCredit/detailCredit';
import { MinimumValueToPayType, TotalAmountType } from './models';

// Config
import {
  DATE_OF_PAYMENT_CONSTANT,
  PAYMENT_TYPE_ENUMERATION,
  PRODUCT_ORIGIN_ENUMERATION,
  PRODUCT_STATE_ENUMERATION,
  PRODUCT_TYPE_ENUMERATION,
} from './config';
import { environment } from '@environments/environment';

interface ValueToPayRadioType {
  detail: {
    selectedOption: {
      value: string;
    };
  };
}

@Component({
  selector: 'app-value-to-pay',
  templateUrl: './value-to-pay.component.html',
  styleUrls: ['./value-to-pay.component.css'],
})
export class ValueToPayComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('showPayableValue', { static: true }) private showPayableValue!: ElementRef;

  private readonly route: ActivatedRoute;
  private readonly router: Router;
  private readonly webElementService: WebcomponentsService;
  private readonly valueToPayService: ValueToPayService;
  private readonly localService: LocalService;
  private readonly authenticationService: AuthService;
  private readonly unSubscribe$: Subject<void>;
  private readonly loanId: string;
  private readonly previousNovationTypeId: string;

  public inmediatePaymentDate: Date;
  public miniPaymentPSE: string;
  public payloadClient: Payload;

  constructor(private readonly injector: Injector) {
    this.route = this.injector.get(ActivatedRoute);
    this.router = this.injector.get(Router);
    this.webElementService = this.injector.get(WebcomponentsService);
    this.valueToPayService = this.injector.get(ValueToPayService);
    this.localService = this.injector.get(LocalService);
    this.authenticationService = this.injector.get(AuthService);

    this.unSubscribe$ = new Subject();

    this.loanId = this.route.snapshot.params['id'];
    this.previousNovationTypeId = this.route.snapshot.queryParams['novationTypeId'];

    this.miniPaymentPSE = '';
  }

  public ngOnInit(): void {
    this.initializeConfig();
    this.initializePayload();
  }

  public ngOnDestroy(): void {
    this.unSubscribe$.complete();
    this.unSubscribe$.unsubscribe();
  }

  public ngAfterViewInit(): void {
    this.webElementService.isLoaded$.subscribe((loaded) => {
      if (loaded) {
        this.returnValueToPay();
      }
    });
  }

  private returnValueToPay(): void {
    forkJoin([
      this.valueToPayService.getMinimumPayment(this.loanId),
      this.valueToPayService.getAmount(this.loanId),
      this.valueToPayService.getDetailsCredit(this.loanId),
    ])
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(
        ([valueToPay, totalValueToPay, creditDetail]: [
          MinimumValueToPayType,
          TotalAmountType,
          { response: DetailCredit }
        ]) => {
          const stateCredit = creditDetail.response.header.stateCredit;
          this.showPayableValue.nativeElement.ngElementStrategy?.componentRef.instance.build({
            params: {
              loanId: this.loanId,
              clientId: this.payloadClient.clienteId,
              product: PRODUCT_TYPE_ENUMERATION.RP,
              origin: PRODUCT_ORIGIN_ENUMERATION.RP,
              totalValueToPay: totalValueToPay.totalAmount,
              minimumValueToPay: valueToPay.totalValueToPay,
              referenceId: this.loanId,
              dateOfPayment: this.getLimitPaymentDate(stateCredit) ?? DATE_OF_PAYMENT_CONSTANT,
              paymentType: this.getPaymentType(stateCredit),
            },
            showCard: true,
          });
        }
      );
  }

  public cancelAction(): void {
    this.router.navigate(['/home']);
  }

  private getLimitPaymentDate(validateLimitPayStatedate: string): string | Date {
    if (
      validateLimitPayStatedate === PRODUCT_STATE_ENUMERATION.OVERDUE ||
      validateLimitPayStatedate === PRODUCT_STATE_ENUMERATION.NOVATION_PENDING
    ) {
      return DATE_OF_PAYMENT_CONSTANT;
    } else if (validateLimitPayStatedate === PRODUCT_STATE_ENUMERATION.DISBURSEMENT) {
      return this.inmediatePaymentDate;
    }
  }

  private getPaymentType(validateLimitPayStatedate: string): string {
    const isRenegotiation = this.previousNovationTypeId === environment.novationTypeId.RNE;
    const isNovationPending = validateLimitPayStatedate === PRODUCT_STATE_ENUMERATION.NOVATION_PENDING;

    if (isRenegotiation) {
      return isNovationPending ? PAYMENT_TYPE_ENUMERATION.RENEGOTIATION : PAYMENT_TYPE_ENUMERATION.REGULAR;
    }

    return isNovationPending ? PAYMENT_TYPE_ENUMERATION.NOVATION : PAYMENT_TYPE_ENUMERATION.REGULAR;
  }

  private initializeConfig(): void {
    this.valueToPayService
      .getDetailsCredit(this.loanId)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (detailResponse: { response: DetailCredit }) => {
          const { stateActual } = detailResponse.response;
          this.inmediatePaymentDate = stateActual.limitDate;
        },
      });
  }

  private initializePayload(): void {
    const token = this.localService.getJsonValue('token');
    this.payloadClient = this.authenticationService.getTokenData(token);
  }

  public valueToPayAction(event: ValueToPayRadioType): void {
    this.miniPaymentPSE = event.detail.selectedOption.value;
  }
}
